/**
 * Shared access to the KVS AI analytics API.
 *
 * The scope headers decide what the caller is allowed to see, so they are
 * defined once. Two copies would drift, and the copy that drifts is the one
 * that quietly widens someone's access.
 */

export const AI_BASE_URL = process.env.REACT_APP_AI_API_URL || "";
const AI_API_KEY = process.env.REACT_APP_AI_API_KEY || "";

/**
 * A handful of accounts already carry a role name the analytics API
 * understands directly — honour those exactly before falling back to the
 * coarser level classification below.
 */
const EXACT_ROLE_MAP: Record<string, string> = {
  admin: "admin", principal: "principal", teacher: "teacher",
  analyst: "analyst", student: "student",
};

// Mirrors kvs_vsk_backend/src/shared/auth/roles.ts. Login role codes are
// dotted per-account strings — "HQ.11", "AD.111", "KV.1409" — not the bare
// level name, and 'AD' (admin test accounts) is documented there as HQ-level
// with full national scope. The exact-string map this replaced had no entry
// for "hq.11" or "ad.111", either of which falls through its `?? "analyst"`
// default — a school-scoped role that REQUIRES a school id. An HQ account
// normally has none, so the very first query was refused before it reached
// SQL generation: HQ.11 happened to log in with role "HQ" (no suffix, an
// exact match) and worked, AD.111 carries the suffix and was blocked outright.
// 'CM' (Commissioner, username CMSNR.NNNN) is national-level like HQ — the
// backend's isNationalLevel() treats the two identically. Without it CM falls
// through to "analyst" below, a school-scoped role that needs a school id a
// Commissioner does not have, so every query is refused.
const LEGACY_LEVEL: Record<string, "HQ" | "RO" | "KV"> = { "0": "HQ", "1": "RO", "2": "KV" };
const HQ_PREFIXES = ["HQ", "AD", "CM"];
const RO_PREFIXES = ["RO"];
const KV_PREFIXES = ["KV"];

function matchesPrefix(role: string, prefixes: string[]): boolean {
  return prefixes.some((p) => role === p || role.startsWith(`${p}.`));
}

function scopeLevel(rawRole: string): "HQ" | "RO" | "KV" | null {
  const normalized = rawRole.trim().toUpperCase();
  if (!normalized) return null;
  if (LEGACY_LEVEL[normalized]) return LEGACY_LEVEL[normalized];
  if (matchesPrefix(normalized, HQ_PREFIXES)) return "HQ";
  if (matchesPrefix(normalized, RO_PREFIXES)) return "RO";
  if (matchesPrefix(normalized, KV_PREFIXES)) return "KV";
  return null;
}

/** The raw login role code to the role the analytics API enforces scope with. */
export function classifyRole(rawRole: string): string {
  const exact = EXACT_ROLE_MAP[rawRole.trim().toLowerCase()];
  if (exact) return exact;
  const level = scopeLevel(rawRole);
  if (level === "HQ") return "admin";
  // RO (regional office) oversees many schools, not one — the analytics API
  // has no region-scoped role yet, so this still lands on the school-scoped
  // "analyst" and needs a schoolId to answer anything. Not a new gap: the
  // previous map made the same choice for "ro".
  return "analyst";
}

/** Read fresh on every call so a role or school change lands immediately. */
export function aiHeaders(): Record<string, string> {
  const rawRole  = localStorage.getItem("userRole") || "";
  const schoolId = localStorage.getItem("schoolId") || "";
  const regionId = localStorage.getItem("regionId") || "";
  return {
    "Content-Type":      "application/json",
    "X-API-Key":         AI_API_KEY,
    "X-User-ID":         localStorage.getItem("username")   || "user",
    "X-User-Role":       classifyRole(rawRole),
    "X-Organization-ID": localStorage.getItem("userRoleId") || "kvs-hq",
    ...(schoolId ? { "X-School-ID": schoolId } : {}),
    ...(regionId ? { "X-Region-ID": regionId } : {}),
  };
}
